import { PlayingCardDTO } from "../api/DTO";
import { CardDeckType, UpdatePreferencesRequest } from "../api/Requests";
import { AppSettingsStorage } from "../localstorage/AppSettingsStorage";
import { HttpClient } from "../api/HttpClient";
import { User } from "../api/models/User";

export interface ModeratorPreferences {
  defaultCardDeckType?: CardDeckType;
  defaultPlayingCards?: PlayingCardDTO[];
}

export class PreferencesService {
  private appSettingsStorage: AppSettingsStorage;
  private httpClient?: HttpClient;

  constructor(authToken?: string) {
    this.appSettingsStorage = new AppSettingsStorage();
    if (authToken) {
      this.httpClient = new HttpClient(authToken);
    }
  }

  /**
   * Save preferences to both account (if authenticated) and local storage
   */
  async savePreferences(
    cardDeckType: CardDeckType,
    playingCards: PlayingCardDTO[],
    user?: User
  ): Promise<void> {
    // Always save to local storage for immediate use
    this.appSettingsStorage.SaveDefaultCardDeckType(cardDeckType);
    this.appSettingsStorage.SaveDefaultPlayingCards(playingCards);

    // If user is authenticated, also save to account
    if (user && this.httpClient) {
      try {
        const request: UpdatePreferencesRequest = {
          defaultCardDeckType: cardDeckType,
          defaultPlayingCards: playingCards,
        };
        await this.httpClient.put("/users/preferences", request);
      } catch (error) {
        console.error("Failed to save preferences to account:", error);
        // Continue even if account save fails - local storage is saved
      }
    }
  }

  /**
   * Load preferences - from account if authenticated, otherwise from local storage
   * Returns empty preferences object if nothing is found (ensures backward compatibility)
   */
  async loadPreferences(user?: User): Promise<ModeratorPreferences> {
    // If authenticated, try to load from account first
    if (user && this.httpClient) {
      try {
        const preferences = (await this.httpClient.get(
          "/users/preferences"
        )) as ModeratorPreferences;
        
        // If account has preferences, sync to local storage and return
        // Check for both undefined and null to handle empty objects from backend
        if (preferences && preferences.defaultCardDeckType !== undefined && preferences.defaultCardDeckType !== null) {
          this.appSettingsStorage.SaveDefaultCardDeckType(
            preferences.defaultCardDeckType
          );
          if (preferences.defaultPlayingCards && preferences.defaultPlayingCards.length > 0) {
            this.appSettingsStorage.SaveDefaultPlayingCards(
              preferences.defaultPlayingCards
            );
          }
          return preferences;
        }
      } catch (error) {
        console.error("Failed to load preferences from account:", error);
        // Fall through to local storage
      }
    }

    // Fallback to local storage (for anonymous users or if account load fails)
    const cardDeckType = this.appSettingsStorage.GetDefaultCardDeckType();
    const playingCards = this.appSettingsStorage.GetDefaultPlayingCards();

    return {
      defaultCardDeckType: cardDeckType ?? undefined,
      defaultPlayingCards: playingCards && playingCards.length > 0 ? playingCards : undefined,
    };
  }

  /**
   * Sync preferences from account to local storage (called on sign-in)
   * Only syncs if preferences exist (handles users with no saved preferences gracefully)
   */
  async syncPreferencesFromAccount(_user: User): Promise<void> {
    if (!this.httpClient) {
      return;
    }

    try {
      const preferences = (await this.httpClient.get(
        "/users/preferences"
      )) as ModeratorPreferences;

      // Only sync if preferences actually exist (not empty object)
      // Check for both undefined and null to handle empty objects from backend
      if (preferences && preferences.defaultCardDeckType !== undefined && preferences.defaultCardDeckType !== null) {
        this.appSettingsStorage.SaveDefaultCardDeckType(
          preferences.defaultCardDeckType
        );
      }
      if (preferences && preferences.defaultPlayingCards && preferences.defaultPlayingCards.length > 0) {
        this.appSettingsStorage.SaveDefaultPlayingCards(
          preferences.defaultPlayingCards
        );
      }
      // If preferences are empty/null, do nothing (user has no saved preferences yet)
    } catch (error) {
      console.error("Failed to sync preferences from account:", error);
      // Don't throw - this is a non-critical operation
    }
  }
}

